import { useEffect, useState } from "react";
import api from "../api/api";
import SideNavLayout from "../customsidenav/sidenavlayout";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Add this import
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId } from "../config/practice";

dayjs.extend(utc);
dayjs.extend(timezone);

const Prices = () => {
  const practiseid = getPracticeId();

  const [lookupdata, setLookupdata] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [updateLookup, setUpdateLookup] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentAction, setCurrentAction] = useState('edit'); // 'edit' or 'delete'

  // Toast states
  const [toastSeverity, setToastSeverity] = useState('success');
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleToastClose = () => {
    setOpenToast(false);
  };

  const fetchdata = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/${practiseid}/lookup_master/get`);
      const data = response.data || [];
      setLookupdata(data);
      setFilteredLogs(data);
    } catch (error) {
      console.log(error);
      showToast('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchdata();
  }, []);

  // SEARCH FILTER
  useEffect(() => {
    const filtered = lookupdata.filter((item) =>
      item.content?.toString().toLowerCase().includes(search.toLowerCase())
    );
    setFilteredLogs(filtered);
    setPage(1);
  }, [search, lookupdata]);

  // PAGINATION
  useEffect(() => {
    const start = (page - 1) * limit;
    const end = start + limit;
    setLogs(filteredLogs.slice(start, end));
  }, [page, limit, filteredLogs]);

  const totalCount = filteredLogs.length;
  const totalPages = Math.ceil(totalCount / limit);

  // Helper function for toast notifications
  const showToast = (message, severity = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };


    // Handle create Click
  const handleCreate = () => {
    setSelectedItem({ context: '', insurance_cost: '', selfpay_cost: '' });
    setCurrentAction('insert');
    setUpdateLookup(true);
  };

  // Handle Edit Click
  const handleEdit = (item) => {
    setSelectedItem({ ...item });
    setCurrentAction('edit');
    setUpdateLookup(true);
  };

  // Handle Delete Click
  const handleDelete = (item) => {
    setSelectedItem(item);
    setCurrentAction('delete');
    setUpdateLookup(true);
  };

  // Handle Insert
  const handleInsert = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/v1/${practiseid}/lookup_master/insert`, {
        context: selectedItem.context,
        content: selectedItem.content,
        insurance_cost: isNaN(parseFloat(selectedItem.insurance_cost)) ? 0.00 : parseFloat(selectedItem.insurance_cost).toFixed(2),
        selfpay_cost: isNaN(parseFloat(selectedItem.selfpay_cost)) ? 0.00 : parseFloat(selectedItem.selfpay_cost).toFixed(2),
      });
      
      if (response.status === 200) {
        showToast('Price has been created successfully!');
        fetchdata(); // Refresh data
        closeModal();
      } else {
        showToast('Failed to insert', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Insert failed', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Handle Save/Update
  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/v1/${practiseid}/lookup_master/update/${selectedItem.id}`, {
        id: selectedItem.id,
        insurance_cost: isNaN(parseFloat(selectedItem.insurance_cost)) ? 0.00 : parseFloat(selectedItem.insurance_cost).toFixed(2),
        selfpay_cost: isNaN(parseFloat(selectedItem.selfpay_cost)) ? 0.00 : parseFloat(selectedItem.selfpay_cost).toFixed(2),
      });
      
      if (response.status === 200) {
        showToast('Updated successfully!');
        fetchdata(); // Refresh data
        closeModal();
      } else {
        showToast('Failed to update', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Update failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Confirmation
  const handleDeleteConfirmed = async () => {
    try {
      setLoading(true);
      const response = await api.delete(`/v1/${practiseid}/lookup_master/${selectedItem.id}`);
      
      if (response.status === 200) {
        showToast('Deleted successfully!');
        fetchdata();
        closeModal();
      } else {
        showToast('Failed to delete', 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Delete failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Close modal and reset
  const closeModal = () => {
    setUpdateLookup(false);
    setSelectedItem(null);
    setCurrentAction('edit');
  };


  const orderTypes = [
    { value: "labs", label: "Labs" },
    { value: "diagnosis", label: "Diagnosis" },
    { value: "injection", label: "Injection" },
    { value: "mrecord", label: "Medical Record" },
    { value: "procedure", label: "Procedure" },
    { value: "vitamin", label: "Vitamin" }
  ];


  return (
    <SideNavLayout>
      <div className="p-6">
        <div className="bg-white rounded-xl shadow border border-gray-200">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Price details</h2>
            <div>
              <input
                type="text"
                placeholder="Enter to search ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <ControlPointIcon className="m-2" onClick={() => handleCreate()} titleAccess="Create New Price"/>
            </div> 
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="border-b text-gray-600 bg-gray-50">
                <tr>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Content</th>
                  <th className="px-6 py-3 pl-[4%]">Insurance Cost</th>
                  <th className="px-6 py-3">Selfpay Cost</th>
                  <th className="px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-3 font-semibold text-gray-800">{item.context}</td>
                    <td className="px-6 py-3 font-semibold text-gray-800">{item.content}</td>
                    <td className="px-6 py-3">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                        ${parseFloat(item.insurance_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs">
                        ${parseFloat(item.selfpay_cost || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3 flex gap-2">
                      <EditIcon
                        className="w-5 h-5 text-blue-500 cursor-pointer hover:text-blue-700"
                        onClick={() => handleEdit(item)}
                        titleAccess="Edit"
                      />
                      <DeleteIcon
                        className="w-5 h-5 text-red-500 cursor-pointer hover:text-red-700"
                        onClick={() => handleDelete(item)}
                        titleAccess="Delete"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {logs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No price details found
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center px-6 py-4">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * limit + 1} to{" "}
              {Math.min(page * limit, totalCount)} of {totalCount}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                Rows per page
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Prev
                </button>
                <div className="px-4 py-1 bg-black text-white rounded">
                  {page}
                </div>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
      </div>

      {/* Loader */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Edit/Delete Modal */}
      {updateLookup && selectedItem && (
        <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white pb-3 rounded-lg shadow-md w-[40%] max-w-md">
            <div className="bg-[#E179545E] p-2 py-3">
              <div className="w-full flex items-center justify-between px-2">
                <span className="ml-2 font-semibold text-[#1D2951] text-base">
                  {currentAction === 'insert' ? 'Add Price' : currentAction === 'edit' ?  'Edit Price' : 'Delete Price'}
                </span>
                <button 
                  className="text-[#1D2951] hover:text-gray-700 text-xl leading-none mr-1" 
                  onClick={closeModal}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4">
              { currentAction === 'insert' ? (
                // Insert Form
                <>

              {/* Price Type Dropdown */}
              <div className="my-4">
                <label className="block text-sm font-semibold text-start text-gray-700">
                  Type
                </label>
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={selectedItem?.context || ''}
                  onChange={(e) =>
                    setSelectedItem({
                      ...selectedItem,
                      context: e.target.value,
                    })
                  }
                >
                  <option value="" disabled>
                    Select Price Type
                  </option>
                  {orderTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

                  <div className="my-4">
                    <label className="block text-sm font-semibold text-start mb-2">Content</label>
                    <input

                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.content}    
                      onChange={(e) =>
                        setSelectedItem({ ...selectedItem, content: e.target.value })
                      }
                    />
                  </div>
                  <div className="my-4">  
                    <label className="block text-sm font-semibold text-start mb-2">Insurance Cost ($)</label>
                    <input
                      type="number"
                      step="0.01" 
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.insurance_cost || ''} 
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          insurance_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-start mb-2">Selfpay Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.selfpay_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          selfpay_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              ) :
              currentAction === 'edit' ? (
                // Edit Form
                <>
                  <div className="my-4">
                    <label className="block text-sm font-semibold text-center text-gray-700">
                      {selectedItem.context}: {selectedItem.content}
                    </label>
                  </div>
                  <div className="my-4">
                    <label className="block text-sm font-semibold text-start mb-2">Insurance Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.insurance_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          insurance_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-start mb-2">Selfpay Cost ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={selectedItem.selfpay_cost || ''}
                      onChange={(e) =>
                        setSelectedItem({
                          ...selectedItem,
                          selfpay_cost: e.target.value === '' ? null : e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              ) : (
                // Delete Confirmation
                <div className="text-center py-8">
                  <div className="text-lg font-semibold text-gray-800 mb-2">
                    Delete "{selectedItem.content}"?
                  </div>
                  <div className="text-sm text-gray-600 mb-6">
                    This action cannot be undone.
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={currentAction === 'insert' ? handleInsert : currentAction === 'edit' ? handleSave : handleDeleteConfirmed}
                  disabled={loading}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    currentAction === 'edit'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Processing...' : (currentAction === 'insert' ? 'Save' : currentAction === 'edit' ? 'Save Changes' : 'Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SideNavLayout>
  );
};

export default Prices;